import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Paper,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { MethodEditorProps } from '../types';

export const WaitForContextEditor: React.FC<MethodEditorProps> = ({ payload, onChange }) => {
  const [newPath, setNewPath] = useState('');

  const contextPaths = payload?.contextPaths || [];
  const checkAllPaths = payload?.checkAllPaths !== false;
  const timeout = payload?.timeout || 30000;
  const pollingInterval = payload?.pollingInterval || 100;

  const addContextPath = () => {
    if (newPath.trim() && !contextPaths.includes(newPath.trim())) {
      onChange({
        ...payload,
        contextPaths: [...contextPaths, newPath.trim()],
      });
      setNewPath('');
    }
  };

  const removeContextPath = (pathToRemove: string) => {
    onChange({
      ...payload,
      contextPaths: contextPaths.filter((path: string) => path !== pathToRemove),
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addContextPath();
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Context Paths to Wait For
      </Typography>

      {/* Add new path */}
      <Box display="flex" gap={1} mb={2}>
        <TextField
          fullWidth
          value={newPath}
          onChange={(e) => setNewPath(e.target.value)}
          onKeyPress={handleKeyPress}
          label="Context Path"
          placeholder="e.g., user.userName, pictures, sdgs"
          helperText="Enter context path to wait for (supports dot notation)"
          size="small"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addContextPath}
          disabled={!newPath.trim() || contextPaths.includes(newPath.trim())}
          sx={{ minWidth: 100 }}
        >
          Add
        </Button>
      </Box>

      {/* Display current paths */}
      {contextPaths.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Waiting for ({contextPaths.length} paths):
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {contextPaths.map((path: string) => (
              <Chip
                key={path}
                label={path}
                onDelete={() => removeContextPath(path)}
                deleteIcon={<DeleteIcon />}
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Wait condition */}
      <FormControlLabel
        control={
          <Checkbox
            checked={checkAllPaths}
            onChange={(e) => onChange({ ...payload, checkAllPaths: e.target.checked })}
          />
        }
        label="Wait for ALL paths (unchecked = wait for ANY path)"
        sx={{ mb: 2 }}
      />

      {/* Timeout configuration */}
      <TextField
        fullWidth
        type="number"
        value={timeout / 1000}
        onChange={(e) => onChange({ ...payload, timeout: Number(e.target.value) * 1000 })}
        label="Timeout (seconds)"
        helperText="Maximum time to wait before giving up"
        margin="normal"
        inputProps={{ min: 1, max: 300 }}
      />

      {/* Polling interval */}
      <TextField
        fullWidth
        type="number"
        value={pollingInterval}
        onChange={(e) => onChange({ ...payload, pollingInterval: Number(e.target.value) })}
        label="Polling Interval (milliseconds)"
        helperText="How often to check for context updates"
        margin="normal"
        inputProps={{ min: 50, max: 5000 }}
      />

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Purpose:</strong> Waits for specific context values to be populated before
          continuing
          <br />
          <strong>Use Case:</strong> Ensure background loading operations complete before accessing
          data
          <br />
          <strong>Condition:</strong> {checkAllPaths ? 'ALL' : 'ANY'} of the specified paths must be
          populated
          <br />
          <strong>Smart Polling:</strong> Uses promise-based checking to avoid React closure issues
        </Typography>
      </Alert>

      {contextPaths.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>No context paths specified!</strong> Add at least one context path to wait for.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};
